import { cn } from "@/utils/cn";
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/**
 * Masks rather than gradient overlays, so the fade works on any background (hovered, selected or tinted rows).
 * Only applied while the content overflows, so text that fits is never faded.
 */
const FADE_RIGHT = "linear-gradient(to right, #000 calc(100% - 1.75rem), transparent)";
const FADE_BOTTOM = "linear-gradient(to bottom, #000 calc(100% - 1.25rem), transparent)";
const maskStyle = (mask: string | null) => (mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined);

function useOverflow(ref: RefObject<HTMLElement | null>, axis: "x" | "y", deps: unknown[]) {
  const [over, setOver] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () =>
      setOver(axis === "x" ? el.scrollWidth > el.clientWidth + 1 : el.scrollHeight > el.clientHeight + 1);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return over;
}

/** One line of text that fades out at the right edge when it doesn't fit; the full text is its tooltip. */
export function FadeText({ children, className, title }: { children: ReactNode; className?: string; title?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const over = useOverflow(ref, "x", [children]);
  return (
    <span
      ref={ref}
      className={cn("block min-w-0 overflow-hidden whitespace-nowrap", className)}
      style={maskStyle(over ? FADE_RIGHT : null)}
      title={over ? (title ?? (typeof children === "string" ? children : undefined)) : title}
    >
      {children}
    </span>
  );
}

/** A text input that fades at the right edge while its value doesn't fit and it isn't being edited. */
export function FadeInput({ className, onFocus, onBlur, ...props }: ComponentPropsWithoutRef<"input">) {
  const ref = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const over = useOverflow(ref, "x", [props.value]);
  return (
    <input
      ref={ref}
      {...props}
      className={className}
      style={maskStyle(over && !editing ? FADE_RIGHT : null)}
      title={over && !editing ? String(props.value ?? "") : props.title}
      onFocus={(e) => {
        setEditing(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setEditing(false);
        onBlur?.(e);
      }}
    />
  );
}

/** A textarea that fades at the bottom while its text runs past the rows shown and it isn't being edited. */
export function FadeTextarea({ className, onFocus, onBlur, ...props }: ComponentPropsWithoutRef<"textarea">) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [editing, setEditing] = useState(false);
  const over = useOverflow(ref, "y", [props.value, props.rows]);
  return (
    <textarea
      ref={ref}
      {...props}
      className={className}
      style={maskStyle(over && !editing ? FADE_BOTTOM : null)}
      onFocus={(e) => {
        setEditing(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setEditing(false);
        onBlur?.(e);
      }}
    />
  );
}
